import React, { useMemo } from 'react';
import { useThunkDispatch } from '../../../../hooks/useThunkDispatch';
import { fetchOrders, getLastQueryData } from 'redux/actions/dataActions/orders/ordersDataActions';
import DefaultDataTable from '../../shared/tables/DefaultDataTable';
import { Filters, SortingRule, Column, Row } from 'react-table';
import IOrder from 'types/backend_models/orders/IOrder';
import TableInputFilter from '../../shared/inputs/TableInputFilter';
import { IFilterProps } from 'types/frontend_models/table/IFilterProps';
import IStatusModel from 'types/backend_models/lookup_status/IStatusModel';
import TablePagination from '../../shared/tables/TablePagination';
import RequestStatus from 'types/general/requests/RequestStatus';
import TableContainer from '../../shared/tables/TableContainer';
import TableLoadingIndicator from '../../shared/tables/TableLoadingIndicator';
import { useHistory } from 'react-router-dom';
import { orderPageRoutes, IRoute } from 'components/navigation/routes';
import { RouteId } from 'types/frontend_models/navigation/RouteId';
import { GenericDataState } from 'redux/reducers/data_action_reducers/generic/GenericDataReducers';
import { useSearchTableQuery } from 'hooks/urlHooks';
import { convertFiltersToTableFilters, IKeyAndOperator, combineTableQueryData, getSorts, defaultDataTablePagesizeOptions, findInitialFilter } from 'functions/tables/TableFunctions';
import { FilterOperators } from 'types/frontend_models/filtering/filters';
import { nameofFactory } from 'functions/misc/NameOf';
import { ITableQueryData } from 'types/frontend_models/table/ITableQueryData';
import { ITableData } from 'types/backend_models/shared/ITableData';
import { toDateString } from 'functions/formatters/date';
import { TableDateRangePicker } from 'components/content/shared/datepickers/TableDateRangePicker';
import { useInitialTableFilterAndSortData } from 'hooks/table/useInitialTableFIlterAndSortData';
import { parseDateRangeString } from 'functions/parsers/DateRangeParsers';
import { queryDifferenceChecker } from 'functions/misc/queryDifferenceChecker';


/* COMPONENT */

interface IProps
{
	orderStatusses: IStatusModel[],
	ordersState: GenericDataState<ITableData<IOrder[]>>
}

function OrdersTable(props: IProps)
{
	const thunkDispatch = useThunkDispatch();
	const history = useHistory();
	const { getTableQueryFromSearch, updateSearchByTableQuery } = useSearchTableQuery();

	function onFiltersChanged(filters: Filters<Object>)
	{
		// console.log('filters', tableFilters);
		// Move table filters into new array in the data format that our data fetch call wants
		const tableFilters = convertFiltersToTableFilters(filters, keysAndOperators);
		const lastQueryData = getLastQueryData();
		const newQueryData = combineTableQueryData({ filters: tableFilters }, lastQueryData);

		// console.log('TrippleDataFetch: onFiltersChanged')
		// to prevent initial triple data fetch
		const isStringDifferent = queryDifferenceChecker({ newQueryData, lastQueryData })
		
		// Update url search
		if(isStringDifferent) updateSearchByTableQuery(newQueryData);
		// Send fetch request
		if(isStringDifferent) thunkDispatch(fetchOrders(newQueryData));
	}
	
	function onSortingChanged(sortingRules: SortingRule<Object>[])
	{
		// return;
		// console.log('sortingRules', sortingRules);
		const sorts = getSorts(sortingRules);
		const lastQueryData = getLastQueryData();
		const newQueryData = combineTableQueryData({ sorted: sorts }, lastQueryData);
		
		// console.log('TrippleDataFetch: onSortingChanged')
		//to prevent initial triple data fetch
		const isStringDifferent = queryDifferenceChecker({ newQueryData, lastQueryData })

		// Update url search
		if(isStringDifferent) updateSearchByTableQuery(newQueryData);
		// Send fetch request
		if(isStringDifferent) thunkDispatch(fetchOrders(newQueryData));

		
	}

	function onPageChange(newPage: number)
	{
		const lastQueryData = getLastQueryData();
		const newQueryData = combineTableQueryData({ currentPage: newPage }, lastQueryData);

		// Update url search
		updateSearchByTableQuery(newQueryData);
		// Send fetch request
		thunkDispatch(fetchOrders(newQueryData));
	}

	function onPageSizeChange(newPage: number, newPageSize: number)
	{
		const lastQueryData = getLastQueryData();
		const newQueryData = combineTableQueryData(
			{ currentPage: newPage, pageSize: newPageSize },
			lastQueryData
		);

		// Update url search
		updateSearchByTableQuery(newQueryData);
		// Send fetch request
		thunkDispatch(fetchOrders(newQueryData));
	}

	const rowClickRoute = useMemo(() =>
	{
		return orderPageRoutes.find(r => r.routeId === RouteId.orderDetail) as IRoute;
	}, []);
	function onRowClick(row: Row<Object>)
	{
		// console.log('row', row);

		let path = rowClickRoute.path + '?id=' + (row.original as IOrder).id.toString();
		history.push(
			path
		);
	}

	const columns: Column<Object>[] = useMemo(() =>
	{
		return createOrderColumns(props.orderStatusses, getTableQueryFromSearch());
	}, [props.orderStatusses, getTableQueryFromSearch]);

	// First time only: Get filters and sorts that are passed in the url = default filter/sort values
	const initialTableFilterAndSortData = useInitialTableFilterAndSortData(getTableQueryFromSearch);

	const noDataMessage =
		props.ordersState.requestStatus === RequestStatus.succes &&
			props.ordersState.data.results.length === 0 ?
			'No orders found.' : undefined;

	// For debugging
	// console.log('orderState data', props.ordersState.data);

	return (
		<TableContainer>
			<DefaultDataTable
				columns={columns}
				data={props.ordersState.data.results}
				pageSize={props.ordersState.data.pageSize}
				rowTooltip={'Click to view this order'}
				initialFilters={initialTableFilterAndSortData.filters}
				initialSortBy={initialTableFilterAndSortData.sortBy}
				onFiltersChanged={onFiltersChanged}
				onSortingChanged={onSortingChanged}
				onRowClick={onRowClick}
				noDataMessage={noDataMessage}
				footer={
					<TablePagination
						currentPage={props.ordersState.data.currentPage}
						pageSize={props.ordersState.data.pageSize}
						recordsCount={props.ordersState.data.rowCount}
						totalPages={props.ordersState.data.pageCount}
						rowsPerPageOptions={defaultDataTablePagesizeOptions}
						onPageChange={onPageChange}
						onRowsPerPageChange={onPageSizeChange}
						dataType={"Orders"}
					/>
				}
			/>
			<TableLoadingIndicator
				display={props.ordersState.requestStatus === RequestStatus.pending}
			/>
		</TableContainer>
	);
};

/* TABLE STRUCTURE & ADDITIONAL FUNCTIONS */

const orderNameOf = nameofFactory<IOrder>();

function createOrderColumns(orderStatusses: IStatusModel[], initialQueryData: ITableQueryData): Column<Object>[]
{
	// let orderStatusSelectOptions: ICustomSelectOptionModel[] = statusToSelectModels(orderStatusses);

	// Column definitions for orders table
	const columns: Column<Object>[] = [
		{
			Header: "Order Nr.",
			accessor: orderNameOf("orderNumber") as keyof Object,
			width: 100,
			maxWidth: 100,
			disableFilters: false,
			filter: 'text',
			Filter: (props: IFilterProps) =>
			{
				const filter = findInitialFilter<IOrder>('orderNumber', initialQueryData.filters);
				let defaultValue: string | undefined = undefined;
				if (filter) {
					defaultValue = filter.value;
				}
				return <TableInputFilter defaultValue={defaultValue} filterDelay={500} {...props} />
			}
		},
		{
			Header: "Order Date",
			accessor: orderNameOf("orderDate") as keyof Object,
			width: 150,
			maxWidth: 150,
			disableFilters: true,
			filter: 'text',
			Filter: (props: IFilterProps) =>
			{
				let defaultValue = undefined;
				const filter = findInitialFilter<IOrder>('orderDate', initialQueryData.filters);
				if (filter) defaultValue = parseDateRangeString(filter.value);

				return (
					<TableDateRangePicker
						defaultValue={defaultValue}
						{...props}
					/>
				);
			},
			Cell: (props: { cell: { value: string | undefined; }; }) =>
			{
				if (!props.cell.value) return '';
				return toDateString(props.cell.value);
			}
		},
		{
			Header: "Your Reference",
			accessor: orderNameOf('customerReference') as keyof Object,
			// minWidth: 250,
			width: 250,
			disableFilters: true,
			filter: 'text',
			Filter: (props: IFilterProps) =>
			{
				const filter = findInitialFilter<IOrder>('customerReference', initialQueryData.filters);
				let defaultValue: string | undefined = undefined;
				if (filter) {
					defaultValue = filter.value;
				}
				return <TableInputFilter defaultValue={defaultValue} filterDelay={500} {...props} />
			}
		},
		// {
		// 	Header: 'Ordered by',
		// 	accessor: orderNameOf('orderedBy') as keyof Object,
		// 	// minWidth: 200,
		// 	width: 200,
		// 	disableSortBy: true,
		// 	// disableFilters: true,
		// 	filter: 'text',
		// 	Filter: (props: IFilterProps) =>
		// 	{
		// 		const filter = findInitialFilter<IOrder>('orderedBy', initialQueryData.filters);
		// 		let defaultValue: string | undefined = undefined;
		// 		if (filter) {
		// 			defaultValue = filter.value;
		// 		}
		// 		return <TableInputFilter defaultValue={defaultValue} filterDelay={500} {...props} />
		// 	}
		// },
		// {
		// 	Header: 'Status',
		// 	accessor: orderNameOf('statusId'),
		// 	filter: 'text',
		// 	// minWidth: 150,
		// 	width: 150,
		// 	// maxWidth: 175,
		// 	Filter: (props: IFilterProps) =>
		// 	{
		// 		// Get selected value from search if any
		// 		let defaultOption: ICustomSelectOptionModel | undefined = undefined;
		// 		// console.log('initialQueryData', initialQueryData);
		// 		const filter = findFilterData('statusId');
		// 		if (filter) {
		// 			defaultOption = orderStatusSelectOptions.find(osso => osso.value === filter.value);
		// 			console.log('defaultOption', defaultOption);
		// 		}
		// 		return (
		// 			<TableSelectFilter
		// 				defaultOption={defaultOption}
		// 				options={orderStatusSelectOptions}
		// 				{...props}
		// 			/>
		// 		);
		// 	},
		// 	Cell: (props) =>
		// 	{
		// 		const stringValue = props.cell.value.toString();
		// 		const status = orderStatusses.find(os => os.id === stringValue);

		// 		if (status) {
		// 			const colorClass = GetOrderStatusColorClass(status.id);
		// 			return (
		// 				<span className={cnTblAlteredCellContent + colorClass}>
		// 					{status.label}
		// 				</span>
		// 			);
		// 		} else {
		// 			return '';
		// 		}
		// 	}
		// },
	];

	return columns;
}

/** Defines which properties use which filter operators in a data fetch request */
const keysAndOperators: IKeyAndOperator[] = [
	{
		key: orderNameOf('orderNumber'),
		operator: FilterOperators.contains
	},
	// {
	// 	key: orderNameOf('orderDate'),
	// 	operator: FilterOperators.contains
	// },
	{
		key: orderNameOf('customerReference'),
		operator: FilterOperators.contains
	},
	{
		key: orderNameOf('orderedBy'),
		operator: FilterOperators.contains
	},
	// {
	// 	key: orderNameOf('statusId'),
	// 	operator: FilterOperators.equals
	// }
]


export default OrdersTable;